import { Link, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import {
  LogOut, Home, FilmIcon, LayersIcon, PaletteIcon, PlusCircle,
  Sparkles, Shield, CreditCard, Coins, ChevronsUpDown, Check, Plus,
  Settings2, ChevronLeft, ChevronRight, PenLine, BookOpen, Share2, Languages,
  CalendarDays, Layers, Menu, X, TrendingUp, Users, Sun, Moon,
  Scale, ChevronDown,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import NewWorkspaceDialog from "@/components/NewWorkspaceDialog";

export default function Layout({ children }) {
  const { user, balance, logout, isOwner } = useAuth();
  const location = useLocation();
  const [workspaces, setWorkspaces] = useState([]);
  const [wsDialogOpen, setWsDialogOpen] = useState(false);
  // Sidebar default: COLLAPSED (icon-only). User can expand and it's remembered.
  const [collapsed, setCollapsed] = useState(() => {
    const stored = localStorage.getItem("mo_sidebar_collapsed");
    return stored === null ? true : stored === "1";
  });
  // Theme: light | dark (default dark). Persisted in localStorage.
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem("mo_theme") === "light" ? "light" : "dark";
  });
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("mo_theme", theme);
  }, [theme]);
  const toggleTheme = () => setTheme((t) => (t === "light" ? "dark" : "light"));
  // Mobile drawer: hidden by default on < lg screens.
  const [mobileOpen, setMobileOpen] = useState(false);
  const idleTimer = useRef(null);

  // Auto-close mobile drawer after 6s of inactivity.
  useEffect(() => {
    if (!mobileOpen) return;
    if (idleTimer.current) clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(() => setMobileOpen(false), 6000);
    return () => idleTimer.current && clearTimeout(idleTimer.current);
  }, [mobileOpen]);

  // Auto-close on route change (also acts as "close on nav-item click").
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Prevent body scroll while drawer is open on mobile.
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const loadWs = async () => {
    try {
      const { data } = await api.get("/workspaces");
      setWorkspaces(data);
    } catch { /* ignore */ }
  };
  useEffect(() => { loadWs(); }, [user?.active_workspace_id]);

  const activeWs = workspaces.find((w) => w.id === user?.active_workspace_id) || workspaces[0];

  // Apply per-workspace accent color as CSS variable
  useEffect(() => {
    if (activeWs?.accent_color) {
      document.documentElement.style.setProperty("--co-amber", activeWs.accent_color);
    }
  }, [activeWs?.accent_color]);

  const toggleCollapse = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem("mo_sidebar_collapsed", next ? "1" : "0");
  };

  const activate = async (wsId) => {
    if (wsId === activeWs?.id) return;
    try {
      await api.post(`/workspaces/${wsId}/activate`);
      toast.success("Workspace switched");
      window.location.reload();
    } catch { toast.error("Switch failed"); }
  };

  // Simplified nav — chat-first UX. Rest of the surfaces remain accessible via
  // direct URL (deep-linked from the chat agent) but hidden from the sidebar.
  const nav = [
    { to: "/", label: "Studio", icon: Home, testid: "nav-home", tags: ["all"] },
    { to: "/pricing", label: "Credits", icon: CreditCard, testid: "nav-pricing", tags: ["all"] },
    { to: "/settings", label: "Settings", icon: Settings2, testid: "nav-settings", tags: ["all"] },
  ];
  if (isOwner) {
    nav.push({ to: "/admin", label: "Admin", icon: Shield, testid: "nav-admin", tags: ["all"] });
  }

  // Persona-aware ordering: items matching the user's persona come first.
  const persona = user?.persona || null;
  const orderedNav = persona
    ? [
        ...nav.filter((n) => n.tags.includes(persona) || n.tags.includes("all")),
        ...nav.filter((n) => !n.tags.includes(persona) && !n.tags.includes("all")),
      ]
    : nav;

  const sidebarW = collapsed ? "80px" : "260px";
  // When the mobile drawer is open, always show labels (drawer is 260px wide,
  // never icon-only on mobile). On desktop, follow `collapsed`.
  const expanded = !collapsed || mobileOpen;

  return (
    <div
      className="relative z-10 min-h-screen lg:grid lg:grid-cols-[var(--sw)_1fr] transition-[grid-template-columns] duration-300"
      style={{ "--sw": sidebarW }}
    >
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          data-testid="mobile-backdrop"
          className="lg:hidden fixed inset-0 z-40 bg-black/70 backdrop-blur-sm animate-in fade-in duration-200"
        />
      )}

      <aside
        data-collapsed={collapsed ? "true" : "false"}
        data-mobile-open={mobileOpen ? "true" : "false"}
        style={{ "--desktop-w": collapsed ? "80px" : "260px" }}
        className={`border-r border-white/5 bg-[#0b0b0d]/95 backdrop-blur-xl py-6 flex flex-col transition-all duration-300 z-50 lg:sticky lg:top-0 lg:h-screen lg:translate-x-0 lg:w-[var(--desktop-w)] fixed inset-y-0 left-0 w-[260px] shadow-2xl ${mobileOpen ? "translate-x-0" : "-translate-x-full"} ${collapsed ? "lg:px-3" : "lg:px-6"} px-6`}
      >
        {/* Brand */}
        <div className={`flex items-center gap-3 mb-6 ${!expanded ? "justify-center" : "justify-between"}`}>
          <Link to="/" className="flex items-center gap-3 group">
            <div className="w-9 h-9 rounded-md bg-gradient-to-br from-[color:var(--co-amber)] to-amber-700 flex items-center justify-center group-hover:scale-105 transition-transform flex-shrink-0">
              <FilmIcon className="w-5 h-5 text-black" strokeWidth={2} />
            </div>
            {expanded && (
              <div>
                <div className="font-heading text-lg font-bold tracking-tight leading-none">
                  {activeWs?.hide_platform_branding ? (activeWs?.company_name || activeWs?.name) : "MediaOthers"}
                </div>
                <div className="font-mono-tech text-[9px] text-zinc-500 mt-1">
                  {activeWs?.hide_platform_branding
                    ? (activeWs?.custom_footer_text || "STUDIO")
                    : "A · QOTHERS · PLATFORM"}
                </div>
              </div>
            )}
          </Link>
          {/* Close button — mobile drawer only */}
          <button
            onClick={() => setMobileOpen(false)}
            data-testid="mobile-menu-close"
            aria-label="Close menu"
            className="lg:hidden p-1.5 rounded-md text-zinc-400 hover:bg-white/5 hover:text-white"
          >
            <X className="w-4 h-4" strokeWidth={1.75} />
          </button>
        </div>

        {/* Workspace switcher */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="workspace-switcher"
              title={activeWs?.name}
              className={`w-full flex items-center gap-3 py-2.5 mb-6 rounded-md border border-white/5 bg-white/[0.02] hover:bg-white/[0.05] hover:border-white/10 transition-colors ${
                !expanded ? "px-2 justify-center" : "px-3"
              }`}>
              <div
                className="w-7 h-7 rounded flex items-center justify-center font-heading font-bold text-xs flex-shrink-0"
                style={{
                  backgroundColor: `${activeWs?.accent_color || "#F5A524"}22`,
                  border: `1px solid ${activeWs?.accent_color || "#F5A524"}55`,
                  color: activeWs?.accent_color || "#F5A524",
                }}>
                {activeWs?.icon || activeWs?.name?.[0]?.toUpperCase() || "W"}
              </div>
              {expanded && (
                <>
                  <div className="flex-1 text-left min-w-0">
                    <div className="font-mono-tech text-[9px] text-zinc-500">WORKSPACE</div>
                    <div className="text-sm font-medium truncate">{activeWs?.name || "Loading…"}</div>
                  </div>
                  <ChevronsUpDown className="w-3.5 h-3.5 text-zinc-500 flex-shrink-0" strokeWidth={1.5} />
                </>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-64 bg-[#1a1a1e] border-white/10 shadow-xl shadow-black/50 z-50">
            <DropdownMenuLabel className="font-mono-tech text-[9px] text-zinc-500">
              YOUR · WORKSPACES
            </DropdownMenuLabel>
            {workspaces.map((w) => (
              <DropdownMenuItem key={w.id} data-testid={`ws-option-${w.id}`}
                onClick={() => activate(w.id)} className="cursor-pointer">
                <div
                  className="w-6 h-6 rounded flex items-center justify-center font-heading font-bold text-[10px] mr-2"
                  style={{
                    backgroundColor: `${w.accent_color || "#F5A524"}22`,
                    border: `1px solid ${w.accent_color || "#F5A524"}55`,
                    color: w.accent_color || "#F5A524",
                  }}>
                  {w.icon || w.name[0]?.toUpperCase()}
                </div>
                <span className="flex-1">{w.name}</span>
                {w.id === activeWs?.id && <Check className="w-3.5 h-3.5 text-emerald-400" strokeWidth={2} />}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator className="bg-white/5" />
            <DropdownMenuItem onClick={() => setWsDialogOpen(true)} data-testid="ws-create"
              className="cursor-pointer text-amber-400">
              <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} /> New workspace
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Nav */}
        <nav className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-1 -mr-2 pr-2 sidebar-scroll">
          {orderedNav.map((n) => {
            const active = n.to === "/" ? location.pathname === "/" : location.pathname.startsWith(n.to);
            const Icon = n.icon;
            return (
              <Link to={n.to} key={n.to} data-testid={n.testid} title={n.label}
                className={`group relative flex items-center gap-3 py-2.5 rounded-md transition-colors duration-200 ${
                  active ? "bg-[color:var(--co-amber)]/10 text-white"
                    : "text-zinc-400 hover:text-white hover:bg-white/5"
                } ${!expanded ? "px-2 justify-center" : "px-3"}`}>
                {active && (
                  <motion.span layoutId="side-active"
                    className="absolute left-0 top-2 bottom-2 w-[3px] rounded-full"
                    style={{ backgroundColor: "var(--co-amber)" }} />
                )}
                <Icon className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
                {expanded && <span className="text-sm font-medium">{n.label}</span>}
                {expanded && n.to === "/admin" && (
                  <span
                    className="ml-auto font-mono-tech text-[8px] px-1.5 py-0.5 rounded"
                    style={{ backgroundColor: "var(--co-amber)33", color: "var(--co-amber)" }}>
                    OWNER
                  </span>
                )}
              </Link>
            );
          })}

          {/* Legals — collapsible, always the last entry in the sidebar nav */}
          <LegalsGroup expanded={expanded} location={location} />
        </nav>

        {/* Footer: user info + collapse toggle */}
        <div className="pt-6 border-t border-white/5 flex-shrink-0">
          {expanded && (
            <div className="text-xs text-zinc-500 mb-3 px-1">
              <div className="text-white font-medium truncate">{user?.name}</div>
              <div className="truncate">{user?.email}</div>
              {user?.referral_code && (
                <div className="mt-2 flex items-center gap-1.5">
                  <Sparkles className="w-3 h-3" strokeWidth={1.5} style={{ color: "var(--co-amber)" }} />
                  <span className="font-mono-tech text-[10px]" style={{ color: "var(--co-amber)" }}>
                    {user.referral_code}
                  </span>
                </div>
              )}
            </div>
          )}
          <div className="flex items-center gap-1">
            <button data-testid="logout-button" onClick={logout} title="Sign out"
              className={`flex items-center gap-2 py-2 rounded-md text-zinc-400 hover:text-white hover:bg-white/5 text-sm transition-colors ${
                !expanded ? "px-2 flex-1 justify-center" : "px-3 flex-1"
              }`}>
              <LogOut className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
              {expanded && <span>Sign out</span>}
            </button>
            <button onClick={toggleTheme} data-testid="theme-toggle"
              title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              aria-label="Toggle theme"
              className="p-2 rounded-md text-zinc-500 hover:text-white hover:bg-white/5 transition-colors">
              {theme === "dark"
                ? <Sun className="w-4 h-4" strokeWidth={1.5} />
                : <Moon className="w-4 h-4" strokeWidth={1.5} />}
            </button>
            <button onClick={toggleCollapse} data-testid="sidebar-collapse-toggle"
              title={collapsed ? "Expand" : "Collapse"}
              className="hidden lg:block p-2 rounded-md text-zinc-500 hover:text-white hover:bg-white/5 transition-colors">
              {collapsed
                ? <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
                : <ChevronLeft className="w-4 h-4" strokeWidth={1.5} />}
            </button>
          </div>
        </div>
      </aside>

      <main className="min-h-screen">
        {/* Compact top bar with credits pill */}
        <div
          data-testid="top-bar"
          className="sticky top-0 z-30 flex items-center justify-between gap-3 px-4 md:px-10 lg:px-14 py-3 bg-[#0b0b0d]/70 backdrop-blur-xl border-b border-white/5"
        >
          {/* Left: hamburger (mobile only) */}
          <button
            onClick={() => setMobileOpen(true)}
            data-testid="mobile-menu-toggle"
            aria-label="Open menu"
            className="lg:hidden p-2 -ml-2 rounded-md text-zinc-300 hover:bg-white/5 transition-colors"
          >
            <Menu className="w-5 h-5" strokeWidth={1.75} />
          </button>
          <div className="hidden lg:block" />

          {/* Right: theme toggle + tier badge + credits pill */}
          <div className="flex items-center gap-2 md:gap-3">
            <button
              onClick={toggleTheme}
              data-testid="theme-toggle-topbar"
              aria-label="Toggle light/dark theme"
              title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              className="p-2 rounded-full border border-white/10 hover:bg-white/5 hover:border-white/20 text-zinc-300 hover:text-white transition-colors"
            >
              {theme === "dark"
                ? <Sun className="w-4 h-4" strokeWidth={1.75} />
                : <Moon className="w-4 h-4" strokeWidth={1.75} />}
            </button>
            {user?.tier && user.tier !== "free" && (
              <span
                data-testid="tier-badge"
                className="font-mono-tech text-[9px] px-2 py-1 rounded uppercase tracking-wider"
                style={{ backgroundColor: "var(--co-amber)22", color: "var(--co-amber)", border: "1px solid var(--co-amber)44" }}>
                {user.tier}
              </span>
            )}
            <Link
              to="/pricing"
              data-testid="credits-pill"
              title="Credits · click to buy more"
              className="flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-mono-tech hover:bg-white/5 transition-colors"
              style={{
                borderColor: "var(--co-amber)44",
                background: "linear-gradient(to right, var(--co-amber)11, transparent)",
              }}
            >
              <Coins className="w-3.5 h-3.5" strokeWidth={1.75} style={{ color: "var(--co-amber)" }} />
              <span className="font-heading font-bold text-sm" style={{ color: "var(--co-amber)" }} data-testid="credits-balance">
                {balance.unlimited ? "∞" : (balance.balance || 0).toLocaleString()}
              </span>
              <span className="text-zinc-500 hidden sm:inline">credits</span>
            </Link>
          </div>
        </div>
        {children}
      </main>

      <NewWorkspaceDialog
        open={wsDialogOpen}
        onOpenChange={setWsDialogOpen}
        onCreated={loadWs}
      />
    </div>
  );
}

/**
 * Legals — collapsible sidebar group holding Privacy, Cookies and Disclaimer.
 * Auto-expands if the current route is /legal/*. Collapsed sidebars show a
 * single Scale icon that jumps to /legal/privacy when clicked.
 */
function LegalsGroup({ expanded, location }) {
  const onLegal = location.pathname.startsWith("/legal");
  const [open, setOpen] = useState(onLegal);
  useEffect(() => { if (onLegal) setOpen(true); }, [onLegal]);

  const items = [
    { to: "/legal/privacy",    label: "Privacy Policy", testid: "legal-privacy" },
    { to: "/legal/cookies",    label: "Cookie Policy",  testid: "legal-cookies" },
    { to: "/legal/disclaimer", label: "Disclaimer",     testid: "legal-disclaimer" },
    { to: "/legal/dpa",        label: "DPA",            testid: "legal-dpa" },
  ];

  // Icon-only sidebar → single Scale icon, no group toggle (tap goes to Privacy).
  if (!expanded) {
    return (
      <Link
        to="/legal/privacy"
        data-testid="nav-legals-collapsed"
        title="Legals"
        className={`group relative flex items-center justify-center py-2.5 px-2 rounded-md transition-colors ${
          onLegal ? "bg-[color:var(--co-amber)]/10 text-white" : "text-zinc-400 hover:text-white hover:bg-white/5"
        }`}
      >
        <Scale className="w-4 h-4" strokeWidth={1.5} />
      </Link>
    );
  }

  return (
    <div className="mt-1" data-testid="legals-group">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="legals-toggle"
        aria-expanded={open}
        className={`w-full flex items-center gap-3 py-2.5 px-3 rounded-md transition-colors text-sm font-medium ${
          onLegal ? "text-white bg-white/[0.02]" : "text-zinc-400 hover:text-white hover:bg-white/5"
        }`}
      >
        <Scale className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
        <span className="flex-1 text-left">Legals</span>
        <ChevronDown
          className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`}
          strokeWidth={1.75}
        />
      </button>
      {open && (
        <div className="pl-3 mt-1 space-y-0.5 border-l border-white/5 ml-4" data-testid="legals-submenu">
          {items.map((it) => {
            const active = location.pathname === it.to;
            return (
              <Link
                key={it.to}
                to={it.to}
                data-testid={it.testid}
                className={`block px-3 py-1.5 rounded-md text-[13px] transition-colors ${
                  active
                    ? "text-amber-400 bg-white/[0.03]"
                    : "text-zinc-500 hover:text-zinc-200 hover:bg-white/5"
                }`}
              >
                {it.label}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

